/**
 * DOM Cache and Management Utility
 * Provides efficient DOM element caching and manipulation to avoid repeated queries
 */

/**
 * Central DOM cache manager
 * Caches frequently accessed DOM elements to improve performance
 */
export class DOMCache {
    constructor() {
        this.cache = new Map();
        this.initialized = false;
    }

    /**
     * Initialize the DOM cache with commonly used elements
     * Should be called once when the DOM is ready
     */
    initialize() {
        if (this.initialized) {
            console.warn('DOMCache already initialized');
            return;
        }

        // Cache main application elements
        this.cacheElement('fractal-canvas', 'fractal-canvas');
        this.cacheElement('control-panel', 'control-panel');
        this.cacheElement('canvas-container', 'canvas-container');
        this.cacheElement('panel-toggle', 'panel-toggle');

        // Cache UI overlays and indicators
        this.cacheElement('level-indicator', 'level-indicator');
        this.cacheElement('current-level', 'current-level');
        this.cacheElement('loading-indicator', 'loading-indicator');

        // Cache control elements (these are created dynamically by ui-controls.js)
        // Removed static caching as these elements don't exist at DOM ready time

        // Cache common selectors
        this.cacheSelector('panel-body', '.panel-body');
        this.cacheSelector('panel-toggle-btn', '.panel-toggle-btn');
        this.cacheSelector('canvas-container-class', '.canvas-container');

        this.initialized = true;
    }

    /**
     * Cache an element by ID
     * @param {string} key - Cache key
     * @param {string} elementId - Element ID to cache
     * @returns {HTMLElement|null} The cached element
     */
    cacheElement(key, elementId) {
        const element = document.getElementById(elementId);
        if (element) {
            this.cache.set(key, element);
        } else {
            console.warn(`Element not found for caching: ${elementId}`);
        }
        return element;
    }

    /**
     * Cache an element by selector
     * @param {string} key - Cache key
     * @param {string} selector - CSS selector
     * @returns {HTMLElement|null} The cached element
     */
    cacheSelector(key, selector) {
        const element = document.querySelector(selector);
        if (element) {
            this.cache.set(key, element);
        } else {
            console.warn(`Element not found for selector: ${selector}`);
        }
        return element;
    }

    /**
     * Get a cached element
     * @param {string} key - Cache key
     * @returns {HTMLElement|null} The cached element or null if not found
     */
    get(key) {
        const element = this.cache.get(key);
        if (!element) {
            console.warn(`Element not in cache: ${key}`);
        }
        return element || null;
    }

    /**
     * Get element with fallback to DOM query (with caching)
     * @param {string} key - Cache key
     * @param {string} selector - Fallback selector or ID
     * @param {boolean} isId - Whether selector is an ID (default: true)
     * @returns {HTMLElement|null} The element
     */
    getWithFallback(key, selector, isId = true) {
        let element = this.cache.get(key);

        if (!element) {
            // Try to find and cache the element
            element = isId ? document.getElementById(selector) : document.querySelector(selector);
            if (element) {
                this.cache.set(key, element);
            }
        }

        return element;
    }

    /**
     * Check if an element exists in cache
     * @param {string} key - Cache key
     * @returns {boolean} True if element exists in cache
     */
    has(key) {
        return this.cache.has(key);
    }

    /**
     * Remove an element from cache
     * @param {string} key - Cache key
     */
    remove(key) {
        this.cache.delete(key);
    }

    /**
     * Clear all cached elements
     */
    clear() {
        this.cache.clear();
        this.initialized = false;
    }

    /**
     * Refresh a cached element (re-query and update cache)
     * @param {string} key - Cache key
     * @param {string} selector - Element selector or ID
     * @param {boolean} isId - Whether selector is an ID (default: true)
     * @returns {HTMLElement|null} The refreshed element
     */
    refresh(key, selector, isId = true) {
        const element = isId ? document.getElementById(selector) : document.querySelector(selector);
        if (element) {
            this.cache.set(key, element);
        } else {
            this.cache.delete(key);
            console.warn(`Element not found during refresh: ${selector}`);
        }
        return element;
    }

    /**
     * Get cache statistics
     * @returns {Object} Cache statistics
     */
    getStats() {
        return {
            size: this.cache.size,
            initialized: this.initialized,
            keys: Array.from(this.cache.keys()),
        };
    }
}

/**
 * Singleton instance of DOMCache
 */
export const domCache = new DOMCache();

/**
 * DOM manipulation utilities with performance optimizations
 */
export class DOMUtils {
    /**
     * Safely update element text content
     * @param {string} elementKey - Cache key or element ID
     * @param {string} text - Text content to set
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static setText(elementKey, text, useCache = true) {
        const element = useCache
            ? domCache.getWithFallback(elementKey, elementKey, true)
            : document.getElementById(elementKey);

        if (element) {
            element.textContent = text;
        } else {
            console.warn(`Element not found for setText: ${elementKey}`);
        }
    }

    /**
     * Safely toggle element class
     * @param {string} elementKey - Cache key or element ID
     * @param {string} className - Class name to toggle
     * @param {boolean} force - Force add (true) or remove (false), or toggle if undefined
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static toggleClass(elementKey, className, force, useCache = true) {
        const element = useCache
            ? domCache.getWithFallback(elementKey, elementKey, true)
            : document.getElementById(elementKey);

        if (element) {
            if (force !== undefined) {
                element.classList.toggle(className, force);
            } else {
                element.classList.toggle(className);
            }
        } else {
            console.warn(`Element not found for toggleClass: ${elementKey}`);
        }
    }

    /**
     * Safely add class to element
     * @param {string} elementKey - Cache key or element ID
     * @param {string} className - Class name to add
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static addClass(elementKey, className, useCache = true) {
        const element = useCache
            ? domCache.getWithFallback(elementKey, elementKey, true)
            : document.getElementById(elementKey);

        if (element) {
            element.classList.add(className);
        } else {
            console.warn(`Element not found for addClass: ${elementKey}`);
        }
    }

    /**
     * Safely remove class from element
     * @param {string} elementKey - Cache key or element ID
     * @param {string} className - Class name to remove
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static removeClass(elementKey, className, useCache = true) {
        const element = useCache
            ? domCache.getWithFallback(elementKey, elementKey, true)
            : document.getElementById(elementKey);

        if (element) {
            element.classList.remove(className);
        } else {
            console.warn(`Element not found for removeClass: ${elementKey}`);
        }
    }

    /**
     * Safely set element style property
     * @param {string} elementKey - Cache key or element ID
     * @param {string} property - Style property name
     * @param {string} value - Style property value
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static setStyle(elementKey, property, value, useCache = true) {
        const element = useCache
            ? domCache.getWithFallback(elementKey, elementKey, true)
            : document.getElementById(elementKey);

        if (element) {
            element.style[property] = value;
        } else {
            console.warn(`Element not found for setStyle: ${elementKey}`);
        }
    }

    /**
     * Batch update multiple elements efficiently
     * @param {Array} updates - Array of update objects with {elementKey, action, ...params}
     * @param {boolean} useCache - Whether to use cache (default: true)
     */
    static batchUpdate(updates, useCache = true) {
        updates.forEach(update => {
            const { elementKey, action, ...params } = update;

            switch (action) {
                case 'setText':
                    DOMUtils.setText(elementKey, params.text, useCache);
                    break;
                case 'toggleClass':
                    DOMUtils.toggleClass(elementKey, params.className, params.force, useCache);
                    break;
                case 'addClass':
                    DOMUtils.addClass(elementKey, params.className, useCache);
                    break;
                case 'removeClass':
                    DOMUtils.removeClass(elementKey, params.className, useCache);
                    break;
                case 'setStyle':
                    DOMUtils.setStyle(elementKey, params.property, params.value, useCache);
                    break;
                default:
                    console.warn(`Unknown batch update action: ${action}`);
            }
        });
    }
}
